/**
 * PICKUPS — what rides a barrel, and what you get for breaking it.
 *
 * ---------------------------------------------------------------------------
 * Why this module exists
 * ---------------------------------------------------------------------------
 * Barrel riders were built as hostile "gold rim-lit elites" on the strength of
 * a line in REFERENCE.md that turned out to be a misread. The footage says
 * otherwise: `reference-media/clip1a` at 0.20 s shows a barrel bursting and
 * THREE soldiers in the player's own blue helmet and pale uniform flying up out
 * of a golden explosion, and at 0.25 s a barrel marked 220 carrying a
 * blue-and-gold MINIGUN hovering over it. Barrels are not cover holding
 * attackers. They are locked boxes, and the thing on top is the prize.
 *
 * So a pickup is a reward with a face: a recruit adds bodies, a weapon adds
 * firepower. That is the crowd-size axis and the damage axis becoming separate
 * things you can be lopsided in, which is the mechanic the pacing proposal
 * describes and could not previously express — `tierFor(troops)` made quality a
 * pure function of quantity, so there was no such thing as a big weak army.
 *
 * ---------------------------------------------------------------------------
 * Shape
 * ---------------------------------------------------------------------------
 * One instanced mesh per kind plus one shared glow sprite: four draw calls for
 * every pickup on screen, whatever the mix. Everything is pooled at boot and
 * nothing allocates in update() or render().
 *
 * The gold rim in the reference is a true outline. This uses a billboard glow
 * disc behind the object instead — at the size a pickup occupies on a phone the
 * two are indistinguishable, and an outline pass would mean either a second
 * pass over every mesh or post-processing, which the frame budget rules out.
 */

import * as THREE from "three";
import { toyMaterial } from "../core/look";
import { CAMERA_LOOK, CAMERA_POS } from "../core/renderer";
import type { System } from "../core/types";

/** What a barrel can be carrying. */
export type PickupKind = "recruit" | "minigun" | "rocket" | "flamer" | "freezer";

export const PICKUP_KINDS: readonly PickupKind[] = [
  "recruit",
  "minigun",
  "rocket",
  "flamer",
  "freezer",
];

/** Live pickups. One per barrel, and barrels cap at 16. */
const CAPACITY = 16;

/** Metres the pickup sits above whatever it is on. Nearly zero: at 0.5 it read
 *  as a floating object rather than as a prize STANDING on the barrel, which is
 *  what the reference shows and what makes the two obviously one thing. */
const HOVER = 0.12;
/** Bob amplitude and rate. A prize should never be as still as scenery. */
const BOB = 0.09;
const BOB_RATE = 2.6;
/** Radians/second of lazy spin, so the silhouette reads from every angle. */
const SPIN_RATE = 1.1;

/** Seconds the collect flight takes to reach the crowd. */
const FLY_TIME = 0.45;
/** Metres the pickup pops upward before it heads for the squad — the reference
 *  throws its recruits clear of the explosion before they travel. */
const FLY_LIFT = 1.6;

/**
 * Diameter of the glow quad, in metres.
 *
 * The ring inside the texture peaks at 0.66 of the radius, so this puts the
 * bright band at ~0.82 m across and the transparent hole at ~0.58 m — just
 * outside a pickup, which is ~0.5 m wide. Sized against the object rather than
 * picked: too small and the ring lands ON the silhouette it is supposed to
 * separate, too large and it reads as unrelated scenery.
 */
const GLOW_SIZE = 1.25;

/** Seconds without a `pin` before a pickup is assumed orphaned and retired.
 *  Two ticks would do; this is generous enough to survive a frame hitch. */
const STALE_TIME = 0.25;

/** Name plate geometry, metres. Wide enough to carry seven characters at a size
 *  that survives the far end of the corridor. */
const LABEL_WIDTH = 2.2;
const LABEL_LIFT = 0.72;

export interface PickupSystem extends System {
  readonly object: THREE.Group;
  /** Place a pickup. Returns its id, or -1 when the pool is full. */
  spawn(kind: PickupKind, x: number, y: number, z: number): number;
  /** Hold a pickup at a world position; call once per tick while its barrel
   *  lives. A pickup in flight ignores this. */
  pin(id: number, x: number, y: number, z: number): void;
  /** What kind a pickup is, or null if the id is not live. */
  kindOf(id: number): PickupKind | null;
  /**
   * Its barrel broke: fly it into the crowd and retire it. The EFFECT is the
   * orchestrator's to apply — this is the flight, not the reward.
   */
  collect(id: number): void;
  /**
   * Take one pickup off the board immediately — no flight, no reward.
   *
   * For the case where its barrel could not be created (the barrel pool is
   * capped at 16). The staleness timer would have caught it a quarter of a
   * second later, and a quarter of a second is fifteen frames of a gold-ringed
   * prize hanging in mid-air with nothing under it.
   */
  retire(id: number): void;
  /** Drop everything. For a run restart. */
  clear(): void;
}

interface Pickup {
  alive: boolean;
  kind: PickupKind;
  x: number;
  y: number;
  z: number;
  prevX: number;
  prevY: number;
  prevZ: number;
  phase: number;
  spin: number;
  /** 0 while riding, ramps to 1 across the collect flight. */
  fly: number;
  /** Where the flight started, so the arc is stable as the crowd moves. */
  fromX: number;
  fromY: number;
  fromZ: number;
  /** Clock reading of the last `pin`. A pickup whose barrel has gone — driven
   *  past un-destroyed, or recycled — stops being pinned and must retire, or
   *  the pool silently fills with prizes nobody can reach and later barrels
   *  spawn carrying nothing. */
  pinnedAt: number;
}

export function createPickups(scene: THREE.Scene): PickupSystem {
  const object = new THREE.Group();
  scene.add(object);

  /**
   * A NAME PLATE OVER EVERY PRIZE.
   *
   * Boxes and spheres have a ceiling on how much they can say, and at the size a
   * barrel occupies out at z −40 a rocket launcher and a minigun are the same
   * grey smudge. Playtest was blunt about it: "I'm not understanding what the
   * items are above the barrels."
   *
   * So the shape carries it as far as it can and the word carries the rest.
   * One plate per kind, one draw call each, only drawn when that kind is on the
   * road. Same heavy white-on-black treatment as the gate numerals, because that
   * is the type in this game that has already been proven to read at distance.
   */
  const labelTex: Record<PickupKind, THREE.CanvasTexture> = {
    recruit: labelTexture("TROOPS", "#8fd4ff"),
    minigun: labelTexture("MINIGUN", "#7fc2ff"),
    rocket: labelTexture("ROCKET", "#ff9a5c"),
    // THE PLATE NAMES THE COUNTER, NOT THE WEAPON. "FLAMER" alone tells a player
    // nothing about when to want one, and the counter table is invisible unless
    // something says it out loud. Two words is all there is room for at this
    // size, so the plate spends them on the ANSWER rather than the name.
    flamer: labelTexture("FLAME vs SWARM", "#ffa23c"),
    freezer: labelTexture("FREEZE: SLOWS", "#7fe4ff"),
  };
  const labels = {} as Record<PickupKind, THREE.InstancedMesh>;
  for (const kind of PICKUP_KINDS) {
    const mat = new THREE.MeshBasicMaterial({
      map: labelTex[kind],
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      // Same rule as the gate numerals: the panel may haze with distance, the
      // words may not. A label you cannot read is not a label.
      fog: false,
    });
    const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, CAPACITY);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.renderOrder = 6;
    labels[kind] = mesh;
    object.add(mesh);
  }

  const geo: Record<PickupKind, THREE.BufferGeometry> = {
    recruit: recruitGeometry(),
    minigun: minigunGeometry(),
    rocket: rocketGeometry(),
    flamer: flamerGeometry(),
    freezer: freezerGeometry(),
  };

  // Vertex colours carry the whole look, so one material serves every kind.
  const bodyMat = toyMaterial({ vertexColors: true });
  const meshes = {} as Record<PickupKind, THREE.InstancedMesh>;
  for (const kind of PICKUP_KINDS) {
    const mesh = new THREE.InstancedMesh(geo[kind], bodyMat, CAPACITY);
    mesh.frustumCulled = false;
    mesh.count = 0;
    meshes[kind] = mesh;
    object.add(mesh);
  }

  const glowTex = glowTexture();
  /**
   * NOT ADDITIVE, and that is the fix for the "objects encased in a white cloud"
   * a playtester reported.
   *
   * Two faults stacked. Additive blending was fogged, and three fogs by mixing
   * the fragment toward the fog colour, which additive then ADDS at full
   * strength — so a distant pickup did not haze out, it acquired a pale sky-blue
   * disc. `barrels.ts` documents that same trap; this predated the rule.
   *
   * Turning fog off exposed the second fault, which is the real one: our sky is
   * `0x7cc4e8`, already at 0.9 in blue. ANY additive colour over it clips to
   * white, so a gold rim light is gold on the road and a white cloud against the
   * sky — and the sky is the background for every pickup more than ~20 m out.
   * Additive rim lights need a dark scene, and this one is bright.
   *
   * Normal alpha blending keeps the ring the colour it is authored, wherever it
   * lands. It is a hollow ring rather than a disc so the pickup's own silhouette
   * still reads through the middle of it.
   */
  const glowMat = new THREE.MeshBasicMaterial({
    map: glowTex,
    transparent: true,
    depthWrite: false,
    fog: false,
  });
  const glow = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), glowMat, CAPACITY);
  glow.frustumCulled = false;
  glow.count = 0;
  glow.renderOrder = 5;
  object.add(glow);

  // The camera never rotates, so the billboard orientation is fixed and can be
  // computed once — same trick the bullet sprites use.
  const viewNormal = new THREE.Vector3().subVectors(CAMERA_POS, CAMERA_LOOK).normalize();
  const glowQuat = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(viewNormal, new THREE.Vector3(), new THREE.Vector3(0, 1, 0)),
  );

  const pool: Pickup[] = [];
  for (let i = 0; i < CAPACITY; i++) {
    pool.push({
      alive: false,
      kind: "recruit",
      x: 0,
      y: 0,
      z: 0,
      prevX: 0,
      prevY: 0,
      prevZ: 0,
      phase: 0,
      spin: 0,
      fly: 0,
      fromX: 0,
      fromY: 0,
      fromZ: 0,
      pinnedAt: 0,
    });
  }

  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const spinAxis = new THREE.Vector3(0, 1, 0);
  let clock = 0;

  function retire(p: Pickup): void {
    p.alive = false;
    p.fly = 0;
  }

  return {
    object,

    spawn(kind, x, y, z) {
      for (let i = 0; i < CAPACITY; i++) {
        const p = pool[i]!;
        if (p.alive) continue;
        p.alive = true;
        p.kind = kind;
        p.x = x;
        p.y = y + HOVER;
        p.z = z;
        p.prevX = p.x;
        p.prevY = p.y;
        p.prevZ = p.z;
        p.phase = i * 1.7;
        p.spin = i * 0.9;
        p.fly = 0;
        p.pinnedAt = clock;
        return i;
      }
      return -1;
    },

    pin(id, x, y, z) {
      const p = pool[id];
      if (!p || !p.alive || p.fly > 0) return;
      p.x = x;
      p.y = y + HOVER;
      p.z = z;
      p.pinnedAt = clock;
    },

    kindOf(id) {
      const p = pool[id];
      return p && p.alive ? p.kind : null;
    },

    collect(id) {
      const p = pool[id];
      if (!p || !p.alive || p.fly > 0) return;
      // Tiny non-zero so the flight starts on the next tick rather than being
      // mistaken for "not flying" by `pin`.
      p.fly = 1e-4;
      p.fromX = p.x;
      p.fromY = p.y;
      p.fromZ = p.z;
    },

    retire(id) {
      const p = pool[id];
      if (p) retire(p);
    },

    clear() {
      for (const p of pool) retire(p);
    },

    update(dt, world) {
      clock += dt;
      for (const p of pool) {
        if (!p.alive) continue;
        p.prevX = p.x;
        p.prevY = p.y;
        p.prevZ = p.z;
        p.spin += SPIN_RATE * dt;

        if (p.fly <= 0 && clock - p.pinnedAt > STALE_TIME) {
          retire(p);
          continue;
        }

        if (p.fly > 0) {
          p.fly = Math.min(1, p.fly + dt / FLY_TIME);
          // Ease toward the crowd, read live so the prize tracks a steering
          // army instead of flying at where it used to be.
          const t = p.fly;
          const ease = t * t * (3 - 2 * t);
          p.x = p.fromX + (world.squadCenter.x - p.fromX) * ease;
          p.z = p.fromZ + (world.squadCenter.z - p.fromZ) * ease;
          // Up first, then down into the crowd — an arc, not a slide.
          p.y = p.fromY + FLY_LIFT * Math.sin(Math.PI * t) - (p.fromY - 1) * ease;
          if (p.fly >= 1) retire(p);
        }
      }
    },

    render(alpha, _world) {
      const counts: Record<string, number> = { recruit: 0, minigun: 0, rocket: 0 };
      const labelCounts: Record<string, number> = { recruit: 0, minigun: 0, rocket: 0 };
      let glowCount = 0;

      for (const p of pool) {
        if (!p.alive) continue;
        const x = p.prevX + (p.x - p.prevX) * alpha;
        const z = p.prevZ + (p.z - p.prevZ) * alpha;
        // Bob only while it is still riding; a collected pickup is already
        // being thrown around and a bob on top of that reads as a wobble.
        const bob = p.fly > 0 ? 0 : Math.sin(clock * BOB_RATE + p.phase) * BOB;
        const y = p.prevY + (p.y - p.prevY) * alpha + bob;
        // Shrink into the crowd at the end of the flight, so it is absorbed
        // rather than clipping through a hundred helmets.
        const s = p.fly > 0 ? 1 - Math.max(0, (p.fly - 0.7) / 0.3) : 1;

        quat.setFromAxisAngle(spinAxis, p.spin);
        pos.set(x, y, z);
        scl.set(s, s, s);
        m.compose(pos, quat, scl);
        const mesh = meshes[p.kind];
        mesh.setMatrixAt(counts[p.kind]!, m);
        counts[p.kind]!++;

        const g = GLOW_SIZE * s;
        pos.set(x, y, z - 0.05);
        scl.set(g, g, g);
        m.compose(pos, glowQuat, scl);
        glow.setMatrixAt(glowCount, m);
        glowCount++;

        // The word sits above the object, billboarded like the glow. Hidden
        // during the collect flight — by then you know what you got.
        if (p.fly <= 0) {
          pos.set(x, y + LABEL_LIFT, z - 0.06);
          scl.set(LABEL_WIDTH, LABEL_WIDTH / 4, 1);
          m.compose(pos, glowQuat, scl);
          labels[p.kind].setMatrixAt(labelCounts[p.kind]!, m);
          labelCounts[p.kind]!++;
        }
      }

      for (const kind of PICKUP_KINDS) {
        const mesh = meshes[kind];
        mesh.count = counts[kind]!;
        mesh.instanceMatrix.needsUpdate = true;
        const label = labels[kind];
        label.count = labelCounts[kind]!;
        label.instanceMatrix.needsUpdate = true;
      }
      glow.count = glowCount;
      glow.instanceMatrix.needsUpdate = true;
    },

    dispose() {
      scene.remove(object);
      for (const kind of PICKUP_KINDS) geo[kind].dispose();
      glow.geometry.dispose();
      for (const kind of PICKUP_KINDS) {
        labels[kind].geometry.dispose();
        (labels[kind].material as THREE.Material).dispose();
        labelTex[kind].dispose();
        labels[kind].dispose();
      }
      bodyMat.dispose();
      glowMat.dispose();
      glowTex.dispose();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Geometry — primitives only, per the asset policy                            */
/* -------------------------------------------------------------------------- */

const HELMET = new THREE.Color(0x3d7fd6);
const SKIN = new THREE.Color(0xf0c9a0);
const UNIFORM = new THREE.Color(0xf2ead6);
const GUNMETAL = new THREE.Color(0x4a5568);
const BRASS = new THREE.Color(0xd8a13a);
const STEEL = new THREE.Color(0x8f9bb0);
const WARHEAD = new THREE.Color(0xd8452f);
/** Matched to KIT_FUEL / KIT_ICE in entities/squad.ts. The object on the barrel
 *  and the object that ends up on a soldier's shoulder have to be recognisably
 *  the same thing, and colour is what carries that at forty pixels. */
const FUEL = new THREE.Color(0xe8622c);
const ICE = new THREE.Color(0x66d8f0);

/** Paint every vertex of `g` one colour and merge it into `parts`. */
function tint(g: THREE.BufferGeometry, colour: THREE.Color): THREE.BufferGeometry {
  const n = g.attributes.position!.count;
  const colours = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    colours[i * 3] = colour.r;
    colours[i * 3 + 1] = colour.g;
    colours[i * 3 + 2] = colour.b;
  }
  g.setAttribute("color", new THREE.BufferAttribute(colours, 3));
  return g;
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  // Hand-merged rather than pulling in BufferGeometryUtils: three's helper lives
  // in the examples bundle, and the whole point of the asset policy is to keep
  // the dependency surface at three.js core plus lil-gui.
  let total = 0;
  for (const p of parts) total += p.attributes.position!.count;
  const position = new Float32Array(total * 3);
  const normal = new Float32Array(total * 3);
  const color = new Float32Array(total * 3);
  let at = 0;
  for (const p of parts) {
    const src = p.toNonIndexed();
    const sp = src.attributes.position!.array as ArrayLike<number>;
    const sn = src.attributes.normal!.array as ArrayLike<number>;
    const sc = src.attributes.color!.array as ArrayLike<number>;
    const count = src.attributes.position!.count;
    for (let i = 0; i < count * 3; i++) {
      position[at * 3 + i] = sp[i]!;
      normal[at * 3 + i] = sn[i]!;
      color[at * 3 + i] = sc[i]!;
    }
    at += count;
    if (src !== p) src.dispose();
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(position.subarray(0, at * 3), 3));
  g.setAttribute("normal", new THREE.BufferAttribute(normal.subarray(0, at * 3), 3));
  g.setAttribute("color", new THREE.BufferAttribute(color.subarray(0, at * 3), 3));
  return g;
}

function at(g: THREE.BufferGeometry, x: number, y: number, z: number): THREE.BufferGeometry {
  g.translate(x, y, z);
  return g;
}

/** A soldier in the player's colours — blue helmet, pale uniform, rifle. The
 *  point is that he is recognisably ONE OF YOURS before you collect him. */
function recruitGeometry(): THREE.BufferGeometry {
  // Full soldier scale. The reference's rider is the same size as a unit in the
  // crowd — that is what makes "one of yours" readable before you collect him.
  const s = 1.0;
  return merge([
    at(tint(new THREE.BoxGeometry(0.34 * s, 0.46 * s, 0.24 * s), UNIFORM), 0, 0.05 * s, 0),
    at(tint(new THREE.SphereGeometry(0.15 * s, 10, 8), SKIN), 0, 0.36 * s, 0),
    at(tint(new THREE.SphereGeometry(0.17 * s, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), HELMET), 0, 0.4 * s, 0),
    at(tint(new THREE.CylinderGeometry(0.19 * s, 0.19 * s, 0.05 * s, 10), HELMET), 0, 0.39 * s, 0),
    at(tint(new THREE.BoxGeometry(0.5 * s, 0.05 * s, 0.05 * s), BRASS), 0.1 * s, 0.12 * s, 0.14 * s),
  ]);
}

/** The reference's barrel-mounted minigun: a rotary barrel cluster on a blocky
 *  receiver, blue and brass. Reads as "more bullets" from its silhouette. */
function minigunGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [
    at(tint(new THREE.BoxGeometry(0.44, 0.39, 0.44), HELMET), 0, 0, 0.12),
    at(tint(new THREE.CylinderGeometry(0.17, 0.17, 0.16, 12), BRASS), 0, 0, -0.06),
    at(tint(new THREE.BoxGeometry(0.21, 0.21, 0.26), GUNMETAL), 0, -0.16, 0.2),
  ];
  // Six barrels around the axis — the read that says "this one spins".
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const b = tint(new THREE.CylinderGeometry(0.042, 0.042, 0.6, 6), STEEL);
    b.rotateX(Math.PI / 2);
    parts.push(at(b, Math.cos(a) * 0.075, Math.sin(a) * 0.075, -0.3));
  }
  return merge(parts);
}

/** A shoulder tube with a fat warhead. Reads as "hits harder", not "shoots
 *  faster" — the silhouette has to carry which upgrade it is. */
function rocketGeometry(): THREE.BufferGeometry {
  const tube = tint(new THREE.CylinderGeometry(0.115, 0.115, 0.92, 10), GUNMETAL);
  tube.rotateX(Math.PI / 2);
  const head = tint(new THREE.ConeGeometry(0.18, 0.38, 10), WARHEAD);
  head.rotateX(-Math.PI / 2);
  const fin = tint(new THREE.BoxGeometry(0.04, 0.28, 0.2), STEEL);
  return merge([
    at(tube, 0, 0, 0),
    at(head, 0, 0, -0.48),
    at(tint(new THREE.BoxGeometry(0.18, 0.18, 0.2), BRASS), 0, -0.02, 0.18),
    at(fin.clone(), 0.1, 0, 0.3),
    at(fin, -0.1, 0, 0.3),
  ]);
}

/**
 * A fat bottle with a flared bell. Short and wide, where every other weapon in
 * the game is long and thin — the silhouette is the message, and this one has to
 * be legible against a rocket launcher at forty pixels.
 */
function flamerGeometry(): THREE.BufferGeometry {
  const tank = tint(new THREE.CylinderGeometry(0.24, 0.24, 0.52, 12), FUEL);
  tank.rotateX(Math.PI / 2);
  const barrel = tint(new THREE.CylinderGeometry(0.1, 0.1, 0.42, 9), GUNMETAL);
  barrel.rotateX(Math.PI / 2);
  const bell = tint(new THREE.CylinderGeometry(0.24, 0.11, 0.24, 12), STEEL);
  bell.rotateX(Math.PI / 2);
  const pilot = tint(new THREE.ConeGeometry(0.08, 0.2, 7), FUEL);
  pilot.rotateX(-Math.PI / 2);
  return merge([
    at(tank, 0, 0, 0.3),
    at(tint(new THREE.CylinderGeometry(0.12, 0.12, 0.12, 9), GUNMETAL), 0, 0, 0.6),
    at(barrel, 0, 0, -0.16),
    at(bell, 0, 0, -0.48),
    at(pilot, 0, 0.16, -0.5),
  ]);
}

/** A slim emitter stacked with coil rings. The rings are the read — a pale tube
 *  without them is just a rifle with the colour turned down. */
function freezerGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const tube = tint(new THREE.CylinderGeometry(0.075, 0.075, 0.86, 9), STEEL);
  tube.rotateX(Math.PI / 2);
  parts.push(at(tube, 0, 0, 0));
  parts.push(at(tint(new THREE.BoxGeometry(0.24, 0.26, 0.32), GUNMETAL), 0, -0.02, 0.32));
  for (let i = 0; i < 3; i++) {
    const ring = tint(new THREE.CylinderGeometry(0.16, 0.16, 0.07, 11), ICE);
    ring.rotateX(Math.PI / 2);
    parts.push(at(ring, 0, 0, 0.06 - i * 0.22));
  }
  const dish = tint(new THREE.CylinderGeometry(0.2, 0.1, 0.16, 11), ICE);
  dish.rotateX(Math.PI / 2);
  parts.push(at(dish, 0, 0, -0.5));
  return merge(parts);
}

/**
 * A gold RING, drawn behind the pickup — the reference's rim outline, one sprite
 * instead of an outline pass.
 *
 * Genuinely hollow: fully transparent out to 0.46, so nothing at all is painted
 * over the object. The earlier version put 18% alpha across the centre, which is
 * a veil over the exact pixels the player is trying to identify — and with
 * additive blending that veil is what turned a distant recruit into a pale blob.
 * A rim light belongs strictly outside the silhouette.
 */
/**
 * A word on a dark plate, in the gate numerals' heavy outlined style.
 *
 * `accent` tints the text toward the thing it names — warm for the rocket, cool
 * for the guns — so the colour agrees with the object below it even before the
 * word itself resolves.
 */
function labelTexture(text: string, accent: string): THREE.CanvasTexture {
  const w = 256;
  const h = 64;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;

  const r = 16;
  ctx.beginPath();
  ctx.moveTo(8 + r, 6);
  ctx.arcTo(w - 8, 6, w - 8, h - 6, r);
  ctx.arcTo(w - 8, h - 6, 8, h - 6, r);
  ctx.arcTo(8, h - 6, 8, 6, r);
  ctx.arcTo(8, 6, w - 8, 6, r);
  ctx.closePath();
  ctx.fillStyle = "rgba(10, 14, 22, 0.82)";
  ctx.fill();

  let size = 36;
  const font = (px: number): string =>
    `900 ${px}px "Arial Black", "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.font = font(size);
  while (ctx.measureText(text).width > w - 44 && size > 16) {
    size -= 2;
    ctx.font = font(size);
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(5, size * 0.2);
  ctx.strokeStyle = "#0a0e16";
  ctx.strokeText(text, w / 2, h / 2 + 1);
  ctx.fillStyle = accent;
  ctx.fillText(text, w / 2, h / 2 + 1);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;
  return tex;
}

function glowTexture(): THREE.CanvasTexture {
  const size = 128;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255, 196, 60, 0)");
  g.addColorStop(0.46, "rgba(255, 196, 60, 0)");
  g.addColorStop(0.66, "rgba(255, 190, 48, 0.72)");
  g.addColorStop(0.82, "rgba(246, 158, 26, 0.34)");
  g.addColorStop(1, "rgba(240, 150, 20, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  // The gradient is authored in sRGB like every other canvas texture here; say
  // so, or three treats it as linear and the gold comes out washed.
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
